/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/ui/utils/tailwind";

export type TextInputProps = {
  name?: string;
  label?: string;
  description?: string;
  placeholder?: string;
  value?: string;
  error?: string;
  required?: boolean;
  optional?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  loading?: boolean;
  type?: "text" | "number" | "email" | "password" | "date" | "time" | "datetime-local";
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void | Promise<void | boolean>;
  onBlur?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void | Promise<void | boolean>;
  className?: string;
  style?: React.CSSProperties;
  id?: string;
  asTextArea?: boolean;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'onBlur' | 'type' | 'value' | 'className' | 'style' | 'id' | 'name' | 'disabled' | 'readOnly' | 'placeholder' | 'required'>;

const TextInput = React.forwardRef<HTMLInputElement | HTMLTextAreaElement, TextInputProps>(({
  name,
  label,
  description,
  placeholder,
  value,
  error,
  required = false,
  optional = false,
  disabled = false,
  readOnly = false,
  loading = false,
  type = "text",
  onChange,
  onBlur,
  className,
  style,
  id,
  asTextArea = false,
  ...rest
}, ref) => {
  const inputId = id || name;
  const { t } = useTranslation('common');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    (onChange as React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>)?.(e);
  };

  const handleBlur = onBlur as
    | ((event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void)
    | undefined;

  const fieldClasses = cn(
    "flex min-h-[44px] w-full items-center border px-3 py-2 text-sm",
    "rounded-(--radius-md)",
    "ring-offset-background placeholder:text-(--text-tertiary)",
    "transition-shadow duration-150",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--input-focus-ring) focus-visible:ring-offset-0 focus-visible:shadow-[0_0_0_3px_var(--input-focus-shadow)]",
    "disabled:cursor-not-allowed disabled:opacity-50",
    "read-only:bg-(--surface-tertiary) read-only:cursor-default",
    error
      ? "border-[var(--status-error)] bg-(--surface-primary)"
      : "border-[var(--border-subtle)] bg-(--surface-secondary)",
    "text-(--text-primary)",
  );

  const fieldStyle = { fontFamily: 'var(--font-family)' };

  return (
    <div className={cn("w-full", className)} style={style}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-xs font-medium mb-1.5 text-(--text-secondary) font-(--font-family) tracking-wide"
        >
          {label}
          {required && <span className="ml-0.5 text-(--status-error)">*</span>}
        </label>
      )}
      {loading ? (
        <div
          data-testid="textinput-skeleton"
          className="animate-pulse bg-[var(--skeleton-base)] min-h-[44px] w-full rounded-(--radius-md)"
        />
      ) : asTextArea ? (
        <textarea
          {...(rest as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
          ref={ref as React.Ref<HTMLTextAreaElement>}
          id={inputId}
          name={name}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          onChange={handleChange}
          onBlur={handleBlur}
          className={fieldClasses}
          style={fieldStyle}
        />
      ) : (
        <input
          {...rest}
          ref={ref as React.Ref<HTMLInputElement>}
          type={type}
          id={inputId}
          name={name}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          onChange={handleChange}
          onBlur={handleBlur}
          className={fieldClasses}
          style={fieldStyle}
        />
      )}
      {!loading && description && !error && (
        <p
          className="mt-1.5 text-xs leading-relaxed text-(--text-tertiary) font-(--font-family)"
        >
          {description}
        </p>
      )}
      {!loading && error && (
        <p
          className="mt-1.5 text-xs leading-relaxed text-(--status-error) font-(--font-family)"
        >
          {error}
        </p>
      )}
    </div>
  );
});

TextInput.displayName = 'TextInput';
export default TextInput;
